'use client'
import { useEffect, useState } from 'react'
import { sendHelpEvent } from '@/lib/help/telemetry'

export type FeedbackStrings = {
  feedbackQuestion: string
  yes: string
  no: string
  thanks: string
}

/**
 * Botones de feedback 👍/👎 por artículo. Island del centro de ayuda (/help).
 * - Emite el evento `feedback` best-effort vía sendHelpEvent.
 * - Anti-doble-voto por navegador: localStorage['help-fb-<slug>'].
 * - Las strings llegan ya resueltas al locale activo (ArticleLayout las arma),
 *   igual que el resto de /help (i18n inline por prop, no next-intl).
 */
export default function FeedbackButtons({
  slug,
  locale,
  t,
}: {
  slug: string
  locale: string
  t: FeedbackStrings
}) {
  const key = `help-fb-${slug}`
  const [voted, setVoted] = useState(false)

  useEffect(() => {
    try {
      if (localStorage.getItem(key)) setVoted(true)
    } catch {
      /* localStorage no disponible: permitir votar */
    }
  }, [key])

  function vote(helpful: boolean) {
    if (voted) return
    setVoted(true)
    try {
      localStorage.setItem(key, helpful ? 'up' : 'down')
    } catch {
      /* best-effort */
    }
    sendHelpEvent({ event: 'feedback', article_slug: slug, helpful, locale })
  }

  return (
    <section className="border-t border-[var(--redy-border-l)] pt-6">
      {voted ? (
        <p className="text-sm font-medium text-[var(--redy-secondary)]">
          {t.thanks}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-[var(--redy-secondary)]">
            {t.feedbackQuestion}
          </span>
          <button
            type="button"
            onClick={() => vote(true)}
            aria-label={t.yes}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--redy-border)] bg-[var(--redy-surface)] px-4 py-1.5 text-sm font-medium text-[var(--redy-charcoal)] transition-colors hover:border-[var(--redy-red)] hover:text-[var(--redy-red)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--redy-red)]"
          >
            <span aria-hidden="true">👍</span> {t.yes}
          </button>
          <button
            type="button"
            onClick={() => vote(false)}
            aria-label={t.no}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--redy-border)] bg-[var(--redy-surface)] px-4 py-1.5 text-sm font-medium text-[var(--redy-charcoal)] transition-colors hover:border-[var(--redy-red)] hover:text-[var(--redy-red)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--redy-red)]"
          >
            <span aria-hidden="true">👎</span> {t.no}
          </button>
        </div>
      )}
    </section>
  )
}
